import * as React from 'react'
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell,
  Typography, Tooltip, Button, Alert} from '@mui/material'
import {dataGridWrapSx, headerCellSx, cellSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, INFO, PRIMARY_BLUE, WARNING} from '../../../styles/foundation/colors'
import {columnDividerSx, declaredFigureSx, prefixSx, dimsSx} from './gridSx'
import {fmtCurrency, fmtKg, EXCESS_NOTE, dimsText} from './format'
import {skuBreakdown} from './state/analytics'
import {governingType} from './state/query'
import {DISPUTE_TYPE} from './data/reference'
import Lightbox from './Lightbox'
import SkuFreezeDrawer from './SkuFreezeDrawer'
// `int` moved into freeze/request.js when the drawer needed the same formatter;
// disputes-v2's format.js has no integer helper and carries a drift-guard test,
// so the one body lives beside the pure logic rather than becoming a second copy.
import {int} from './freeze/request'

const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

// Same governing/non-governing contrast as DisputeGrid.js's Billed column and
// RecordSections.js's WeightComparisonSection — kept local because nothing
// outside a dead/volumetric split ever needs it, exactly as those two files
// each keep their own copy.
const governSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY}

/**
 * The SKU's charged-weight split as one proportional bar — a segment per 0.5 kg
 * slab the courier billed, lightest on the left, WIDTH proportional to how many
 * shipments landed there.
 *
 * Width, not height. This started as a column chart scaled to the tallest bar,
 * which in a 100x26 table cell degenerated badly: 20 of the 25 SKUs are charged
 * at a single weight, so their one column filled the whole box and read as a
 * solid blue rectangle rather than a chart, and a 2-versus-1 split rendered two
 * EQUAL-width columns differing only in a few pixels of height. Sharing the
 * width instead makes the majority visible at a glance in a cell this short,
 * and a fully blue bar now means something true and readable: every shipment
 * was charged the modal weight.
 *
 * Hand-rolled SVG, not Recharts (a dependency here): this renders once per table
 * row, and instantiating a charting library that many times for a bar with no
 * axes, legend or interaction is a real regression. It also keeps the
 * accessibility contract — one role="img" narrating every slab and its count —
 * which a canvas blob would lose.
 *
 * The MODAL slab carries the primary colour and every other slab is amber, the
 * same rule the freeze drawer's SlabColumns and the freeze module's IniSpark
 * use, so "amber means billed at something else" holds wherever a merchant
 * meets it.
 */
function SkuSpark({weights, modal}) {
  const h = (weights || []).slice().sort((a, b) => a.weight - b.weight)
  if (!h.length) return <Typography sx={subtleTextSx}>No data</Typography>

  const W = 100
  const H = 26
  const GAP = 1.5
  const total = h.reduce((n, x) => n + x.count, 0) || 1
  const usable = W - GAP * (h.length - 1)
  const label = h
    .map((x) => `${fmtKg(x.weight)}: ${x.count} ${x.count === 1 ? 'shipment' : 'shipments'}`
      + (x.weight === modal ? ' (most probable)' : ''))
    .join(', ')

  let x = 0
  return (
    <Box
      component='svg'
      role='img'
      aria-label={`Charged-weight distribution — ${label}`}
      viewBox={`0 0 ${W} ${H}`}
      sx={{width: W, height: H, display: 'block'}}
    >
      {h.map((slab) => {
        // A slab that is one shipment in two hundred still has to be visible,
        // so every segment keeps a floor; the running cursor absorbs the
        // rounding rather than letting the segments drift apart.
        const w = Math.max(2, (slab.count / total) * usable)
        const left = x
        x += w + GAP
        return (
          <rect
            key={slab.weight}
            x={left}
            y={0}
            width={w}
            height={H}
            rx='2'
            fill={slab.weight === modal ? PRIMARY_BLUE : WARNING}
          />
        )
      })}
    </Box>
  )
}

/**
 * The Merchant weight cell spoken aloud. The cell's hierarchy — which line is
 * the headline and which two are its components — is carried entirely by font
 * size, weight and colour, none of which a screen reader can see, so the whole
 * breakdown is restated in words. Same shape and same phrasing as
 * DisputeGrid.js's declaredSummary(), but written against skuBreakdown's field
 * names and saying "Merchant weight" because that is what this column is
 * called (M2.15.5's rename). Exported so the claim stays assertable in tests.
 */
export function merchantWeightSummary(s) {
  const d = s.declaredDims || {l: 0, b: 0, h: 0}
  return [
    `Merchant weight ${fmtKg(s.modalWeight)}`,
    `dead weight ${fmtKg(s.modalWeight)}`,
    `volumetric ${fmtKg(s.declaredVolKg)}`,
    `${d.l} by ${d.b} by ${d.h} centimetres`,
  ].join(', ')
}

const DERIVATION_TIP ='The weight couriers charged most often for this SKU, read straight off the ' +
  'charged-weight spread — not a separate estimate. Merchant weight is this SKU\'s modal ' +
  'weight on file.'

/**
 * Recommended weight corrections — Phase D's rebuild of what used to be
 * "Open weight disputes by SKU". Still a plain MUI Table (never the
 * mockup's card list — M2.15): every SKU that has raised a weight
 * discrepancy in the current date range (see index.js's `rows`, the same
 * range-filtered set the KPI strip is built from), sorted by excess freight
 * descending. `openCount` and `exposure` count every dispute ever
 * raised for the SKU, not only its currently-unresolved ones — see
 * state/analytics.js's skuBreakdown for why that matches the dataset's own
 * precomputed SKU roll-up.
 *
 * Merchant weight / Prozo suggested weight are the renamed
 * declared/charged pair (M2.15.5): declared is the SKU's own modal weight,
 * suggested is skuBreakdown's `suggestedWeight` — the mode of the SKU's
 * own charged-weight histogram, i.e. real data, never a fabricated figure.
 * The dims/dead/volumetric split shown beneath it belongs to one real row
 * that actually charged that suggested weight, and governingType() (the
 * SAME helper DisputeGrid.js and RecordSections.js call on a single row)
 * decides which of dead/volumetric bolds — so this table can never disagree
 * with the grid or the drawer about which figure governs for a shared SKU.
 *
 * Merchant weight carries its own dead/volumetric/carton breakdown, mirroring
 * the ledger grid's Declared cell so the same split reads identically on both
 * screens. Its figures come from skuBreakdown's `declaredVolKg`/`declaredDims`,
 * which are captured from the row `modalWeight` itself came from — NOT from the
 * row behind `suggestedWeight`, whose carton belongs to the billed side. That
 * distinction is the whole reason the two triples are separate fields; see
 * state/analytics.js.
 *
 * No confidence score, no `+N` chip weights: Phase D's honesty note flags
 * the mockup's confidence percentage as two counts dressed up as a
 * statistic (`46 + log(shipments)*6.4 + disputed*2.5`), and this dataset
 * has no real per-SKU shipment-volume figure to found a genuine one on
 * either. Dropped rather than relabelled — the sort order (exposure) and
 * the spread column already carry the "how sure should I be" signal
 * honestly.
 *
 * No Select all, no per-row checkboxes (M2.16) — this table never had bulk
 * selection to begin with, so there is nothing to remove; the point is
 * simply that none gets added back.
 *
 * "Freeze SKU" opens `SkuFreezeDrawer`, which is handed the SKU roll-up and the
 * SAME `rows` this table was built from and derives its own coverage from them.
 * It no longer mounts `EvidenceIntake`: a freeze request proposes one weight for
 * a whole SKU, and `EvidenceIntake` submits dimensions and a dead weight against
 * ONE shipment id, which is a different claim. The drawer's own evidence step
 * attaches to the request instead.
 */
export default function SkuIntelligence({rows, skus: skusProp, notice, onRequestFreeze, onOpenFreezeModule, focusFallbackRef, shipmentCounts, fetchSkuHistory}) {
  // `skusProp` is the server's aggregation over the merchant's whole filtered book. Falling back to
  // skuBreakdown keeps the seeded dataset — and every test built on it — working unchanged.
  const derived = React.useMemo(() => skuBreakdown(rows, shipmentCounts), [rows, shipmentCounts])
  const skus = skusProp || derived
  const [freezeSku, setFreezeSku] = React.useState(null)
  const [lightboxSrc, setLightboxSrc] = React.useState(null)

  const handleCloseFreeze = () => {
    setFreezeSku(null)
    // Same focus-fallback mechanism as DisputeDrawer.js/DistributionDrawer.js:
    // this drawer's own trigger is a per-row button in a table that can
    // reflow the instant the underlying `rows` change, so MUI's own
    // focus-restore can land on nothing. When that leaves focus on <body>,
    // fall back to the tab bar instead of letting keyboard focus vanish.
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }

  return (
    <Box>
      <Box sx={{mb: 1.5}}>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY}}>
          Recommended weight corrections
        </Typography>
      </Box>

      {/* Said once, above the table, rather than as a row inside it: a parcel holding several
          products has no single product to attribute the weight to, so it cannot appear here at all.
          Stating the exclusion is the difference between a table that is incomplete and a table that
          is wrong. */}
      {notice && (
        <Alert severity='info' sx={{mb: 1.5, fontFamily: FONT, fontSize: SIZE.xs, py: 0.5, '& .MuiAlert-message': {lineHeight: 1.6}}}>
          {notice}
        </Alert>
      )}

      {skus.length === 0 ? (
        <Box sx={{...dataGridWrapSx, p: 3, textAlign: 'center'}}>
          <Typography sx={subtleTextSx}>No SKU-level weight discrepancies in this date range.</Typography>
        </Box>
      ) : (
        <Box sx={dataGridWrapSx}>
          {/* Same column rules and cell padding as the ledger grid — see
              gridSx.js. This table simply never got them when they were added.
              No :first-of-type exclusion here: unlike DisputeGrid there is no
              leading checkbox column, so the first column is real data and
              earns its divider. */}
          <Table sx={{minWidth: 1120, ...columnDividerSx}}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>SKU</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'center'}}>Shipments</TableCell>
                <TableCell sx={headerCellSx}>Merchant weight</TableCell>
                <TableCell sx={headerCellSx}>
                  <Tooltip title={DERIVATION_TIP}>
                    <Typography
                      component='span' tabIndex={0}
                      sx={{
                        fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, textTransform: 'uppercase',
                        color: SUBTLE, letterSpacing: '0.03em', cursor: 'help',
                        borderBottom: `1px dashed ${SUBTLE}`, display: 'inline-block',
                      }}
                    >
                      Prozo suggested weight
                    </Typography>
                  </Tooltip>
                </TableCell>
                {/* Centred, matching the ledger grid's standalone figures.
                    "Excess freight" rather than "Indicative exposure": it is
                    the same rupee number the grid's Excess freight column
                    shows, summed per SKU, and calling it two things on two
                    screens made it read as two different measures. */}
                <TableCell sx={{...headerCellSx, textAlign: 'center'}}>Open disputes</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'center'}}>Excess freight</TableCell>
                <TableCell sx={headerCellSx}>Weight distribution</TableCell>
                <TableCell sx={headerCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {skus.map((s) => {
                const key = s.sku || s.product
                const sameAsSku = !s.product || s.product === s.sku
                // Null rather than zeros: a SKU with no carton recorded shows no carton line.
                const dims = dimsText(s.dims, ' × ')
                const declaredDims = dimsText(s.declaredDims)
                const gType = governingType(s)
                const deadGoverns = gType === 'HIGHER_DEAD' || gType === 'HIGHER_BOTH'
                const volGoverns = gType === 'HIGHER_VOL' || gType === 'HIGHER_BOTH'

                return (
                  <TableRow key={key} hover>
                    <TableCell sx={cellSx}>
                      <Typography
                        title={s.sku || s.product}
                        sx={{
                          fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY,
                          maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {s.sku || '(no SKU code)'}
                      </Typography>
                      {!sameAsSku && (
                        <Typography
                          title={s.product}
                          sx={{...subtleTextSx, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                        >
                          {s.product}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Mirrors the ledger grid's Declared cell line for line
                        (DisputeGrid.js) — figure, then the Dead/Vol pair, then
                        the carton — so one SKU's declared breakdown never
                        renders two ways on two screens.

                        Dead repeats modalWeight instead of reading a separate
                        field, exactly as the ledger repeats originalWeight: the
                        declared dead weight IS the declared weight, and a single
                        source cannot disagree with itself.

                        Deliberately NO governing tag, unlike the suggested-
                        weight column beside it. The declared volumetric always
                        lands below the declared weight by construction (see the
                        generator's provenance header), so dead would win every
                        SKU and the tag would say nothing. The billed side is
                        where that emphasis carries information.

                        Left-aligned, matching the Prozo suggested weight column
                        beside it and the ledger's own Declared cell. It was
                        centred while it held one lone figure; with three stacked
                        lines the ragged left edge read as a column that had come
                        loose from its neighbour. This is also now byte-identical
                        to the ledger's copy — `justifyContent: 'start'` was the
                        single property the two differed by. */}
                    <TableCell sx={{...cellSx, textAlign: 'center', fontVariantNumeric: 'tabular-nums'}}>
                      {int(s.shipments)}
                    </TableCell>

                    <TableCell sx={cellSx} aria-label={merchantWeightSummary(s)}>
                      <Typography sx={declaredFigureSx}>
                        {fmtKg(s.modalWeight)}
                      </Typography>
                      <Box sx={{
                        display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 0.75,
                        justifyContent: 'start', alignItems: 'baseline', mt: 0.25,
                      }}>
                        <Typography sx={prefixSx}>Dead Wt.</Typography>
                        <Typography sx={{...subtleTextSx, whiteSpace: 'nowrap'}}>{fmtKg(s.modalWeight)}</Typography>
                        <Typography sx={prefixSx}>Vol Wt.</Typography>
                        <Typography sx={{...subtleTextSx, whiteSpace: 'nowrap'}}>{fmtKg(s.declaredVolKg)}</Typography>
                      </Box>
                      {declaredDims && <Typography sx={dimsSx}>{declaredDims}</Typography>}
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
                        {fmtKg(s.suggestedWeight)}
                      </Typography>
                      {dims && <Typography sx={subtleTextSx}>{dims}</Typography>}
                      <Box sx={{display: 'flex', gap: 0.75, mt: 0.25}}>
                        <Typography sx={deadGoverns ? governSx : subtleTextSx}>Dead {fmtKg(s.deadKg)}</Typography>
                        <Typography sx={subtleTextSx}>·</Typography>
                        <Typography sx={volGoverns ? governSx : subtleTextSx}>Vol {fmtKg(s.volKg)}</Typography>
                      </Box>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: INFO, mt: 0.25}}>
                        {DISPUTE_TYPE[gType].label}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{...cellSx, textAlign: 'center'}}>{s.openCount}</TableCell>

                    <TableCell sx={{...cellSx, textAlign: 'center'}}>
                      <Tooltip title={EXCESS_NOTE}>
                        <span>{fmtCurrency(s.exposure)}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <SkuSpark weights={s.weights} modal={s.suggestedWeight} />
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Button
                        size='small'
                        // Live: straight to Merchant Weight Freeze, where the request is raised for real.
                        onClick={() => (onOpenFreezeModule ? onOpenFreezeModule(s) : setFreezeSku(s))}
                        sx={secondaryBtnSx}
                      >
                        Freeze SKU
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <SkuFreezeDrawer
        open={!!freezeSku}
        sku={freezeSku}
        rows={rows}
        onClose={handleCloseFreeze}
        onRequestFreeze={onRequestFreeze}
        onImageClick={setLightboxSrc}
        fetchSkuHistory={fetchSkuHistory}
      />

      {/* `open` was missing here before: Lightbox passes it straight to MUI's Dialog,
          so an undefined `open` meant the courier-scan lightbox in this tab never
          opened at all. DisputeDrawer.js and BillingRecord.js both pass
          `open={!!lightboxSrc}`; this one now matches them. */}
      <Lightbox
        src={lightboxSrc}
        alt='Courier scan image'
        open={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />
    </Box>
  )
}
